import { EDeliverableType } from '@enums/campaign.enum';
import { isDeliverableArrayKey } from './brief-mapping.util';
import { IBriefValidationMessage } from './brief.interface';

export type TBriefStop =
  | { kind: 'basicsDates'; messages: IBriefValidationMessage[] }
  | { kind: 'basicsName'; messages: IBriefValidationMessage[] }
  | { kind: 'basicsNotes'; messages: IBriefValidationMessage[] }
  | { kind: 'basicsBudget'; messages: IBriefValidationMessage[] }
  | { kind: 'platform'; templateIndex: number; messages: IBriefValidationMessage[] }
  | {
      kind: 'deliverable';
      templateIndex: number;
      arrayKey: string;
      itemIndex: number;
      messages: IBriefValidationMessage[];
    }
  | { kind: 'execution'; templateIndex: number; itemIndex: number | null; messages: IBriefValidationMessage[] };

const BASICS_STOP_KIND_BY_FIELD: Record<string, 'basicsDates' | 'basicsName' | 'basicsNotes' | 'basicsBudget'> = {
  budget: 'basicsBudget',
  currency: 'basicsBudget',
  end: 'basicsDates',
  name: 'basicsName',
  notes: 'basicsNotes',
  start: 'basicsDates',
};

// Fixed display order for basics stops, independent of the order their errors happen to appear in
// - Name, then Duration (dates), then Budget, then Notes.
const BASICS_STOP_ORDER: readonly ('basicsName' | 'basicsDates' | 'basicsBudget' | 'basicsNotes')[] = [
  'basicsName',
  'basicsDates',
  'basicsBudget',
  'basicsNotes',
];

// Groups errors into an ordered, category-major list of "stops": campaign basics first, then
// every template's platform issue, then every erroring deliverable item, then every erroring
// execution item. Fixing one thing can surface or remove others, so this is always run fresh
// against the latest errors[] rather than incrementally patched. `campaign` is the draft the errors
// belong to - a whole-template invalid_duration names no game play, so it is read from there.
export function buildBriefStops(
  messages: IBriefValidationMessage[],
  campaign: Record<string, unknown> = {},
): TBriefStop[] {
  const basicsStops = new Map<string, TBriefStop>();
  const platformStops = new Map<number, TBriefStop & { kind: 'platform' }>();
  const deliverableStops = new Map<string, TBriefStop & { kind: 'deliverable' }>();
  const executionStops = new Map<string, TBriefStop & { kind: 'execution' }>();

  for (const message of messages) {
    const tokens = message.pathTokens;

    if (tokens[0] !== 'ctaTemplates') {
      addBasicsMessage(basicsStops, tokens, message);

      continue;
    }

    const [, templateIndex] = tokens;

    if (typeof templateIndex !== 'number') {
      continue;
    }

    if (tokens.length === 2 && message.code === 'no_execution') {
      addExecutionMessage(executionStops, templateIndex, null, message);

      continue;
    }

    if (tokens.length === 2 && message.code === 'invalid_duration') {
      addGamePlaysMessage(deliverableStops, campaign, templateIndex, message);

      continue;
    }

    const [, , field] = tokens;

    if (field === 'platformFeature') {
      addPlatformMessage(platformStops, templateIndex, message);

      continue;
    }

    if (field === 'executions') {
      const itemIndex = typeof tokens[3] === 'number' ? tokens[3] : null;

      addExecutionMessage(executionStops, templateIndex, itemIndex, message);

      continue;
    }

    if (typeof field === 'string' && isDeliverableArrayKey(field) && typeof tokens[3] === 'number') {
      addDeliverableMessage(deliverableStops, templateIndex, field, tokens[3], message);
    }
  }

  return [
    ...BASICS_STOP_ORDER.map((kind) => basicsStops.get(kind)).filter((stop): stop is TBriefStop => !!stop),
    ...Array.from(platformStops.values()).sort((a, b) => a.templateIndex - b.templateIndex),
    ...Array.from(deliverableStops.values()).sort((a, b) => a.templateIndex - b.templateIndex),
    ...Array.from(executionStops.values()).sort((a, b) => a.templateIndex - b.templateIndex),
  ];
}

// Schema-layer basics errors path bare ("end", "name", ...), but business-layer ones nest under
// "campaign." ("campaign.end") - and a whole-range error with no single offending field paths as
// just "campaign". Both need to resolve to the same basics stop as their schema-layer counterpart.
function basicsFieldFromTokens(tokens: (string | number)[]): string | undefined {
  if (tokens[0] === 'campaign') {
    return typeof tokens[1] === 'string' ? tokens[1] : 'start';
  }

  return typeof tokens[0] === 'string' ? tokens[0] : undefined;
}

function addBasicsMessage(
  basicsStops: Map<string, TBriefStop>,
  tokens: (string | number)[],
  message: IBriefValidationMessage,
): void {
  const field = basicsFieldFromTokens(tokens);
  const kind = field ? BASICS_STOP_KIND_BY_FIELD[field] : undefined;

  if (!kind) {
    return;
  }

  const existing = basicsStops.get(kind);

  if (existing) {
    existing.messages.push(message);

    return;
  }

  basicsStops.set(kind, { kind, messages: [message] });
}

function addPlatformMessage(
  platformStops: Map<number, TBriefStop & { kind: 'platform' }>,
  templateIndex: number,
  message: IBriefValidationMessage,
): void {
  const existing = platformStops.get(templateIndex);

  if (existing) {
    existing.messages.push(message);

    return;
  }

  platformStops.set(templateIndex, { kind: 'platform', messages: [message], templateIndex });
}

function addDeliverableMessage(
  deliverableStops: Map<string, TBriefStop & { kind: 'deliverable' }>,
  templateIndex: number,
  arrayKey: string,
  itemIndex: number,
  message: IBriefValidationMessage,
): void {
  const key = `${templateIndex}:${arrayKey}:${itemIndex}`;
  const existing = deliverableStops.get(key);

  if (existing) {
    existing.messages.push(message);

    return;
  }

  deliverableStops.set(key, { arrayKey, itemIndex, kind: 'deliverable', messages: [message], templateIndex });
}

// The AI flow lengthens a short execution itself (fitExecutionDurationsToGamePlays), so an
// invalid_duration that still arrives means the game plays together outrun the campaign end - only
// shortening them fixes it, so every game play of that template becomes a stop.
function addGamePlaysMessage(
  deliverableStops: Map<string, TBriefStop & { kind: 'deliverable' }>,
  campaign: Record<string, unknown>,
  templateIndex: number,
  message: IBriefValidationMessage,
): void {
  const ctaTemplate = ((campaign['ctaTemplates'] as Record<string, unknown>[]) ?? [])[templateIndex];
  const gamePlaysCount = ((ctaTemplate?.['deliverables'] as Record<string, unknown>[]) ?? []).filter(
    (deliverable) => deliverable['type'] === EDeliverableType.gamePlay,
  ).length;

  for (let itemIndex = 0; itemIndex < gamePlaysCount; itemIndex += 1) {
    addDeliverableMessage(deliverableStops, templateIndex, 'gamePlays', itemIndex, message);
  }
}

function addExecutionMessage(
  executionStops: Map<string, TBriefStop & { kind: 'execution' }>,
  templateIndex: number,
  itemIndex: number | null,
  message: IBriefValidationMessage,
): void {
  const key = `${templateIndex}:${itemIndex ?? 'new'}`;
  const existing = executionStops.get(key);

  if (existing) {
    existing.messages.push(message);

    return;
  }

  executionStops.set(key, { itemIndex, kind: 'execution', messages: [message], templateIndex });
}
